import { X, Check, Zap, Target, Lightbulb } from "lucide-react";

const ProblemSolution = () => {
  const problems = [
    "Just solving 500 DSA questions isn't enough.",
    "Your CGPA alone won't save you.",
    'Sending "Hi, is there a vacancy?" messages won\'t get replies.',
    "Uploading your resume on job portals won't bring callbacks.",
  ];

  return (
    <div className="bg-black py-32 lg:py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="space-y-12">
          {/* Problems Section */}
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                The Hard Truth About Tech Internships
              </h2>
            </div>

            <div className="grid gap-4 md:gap-6">
              {problems.map((problem, index) => (
                <div
                  key={index}
                  className="group relative bg-red-900/10 border border-red-500/30 rounded-lg p-4 md:p-6 hover:border-red-400/50 transition-all duration-300"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-red-500/20 to-pink-500/20 rounded-lg blur opacity-0 group-hover:opacity-30 transition duration-300" />

                  <div className="relative flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 shrink-0">
                      <X className="w-5 h-5 text-red-400" />
                    </div>
                    <p className="text-gray-300 text-lg group-hover:text-white transition-colors">
                      {problem}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Solution Section */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-900/20 to-blue-900/20 border border-green-500/30 p-6 md:p-8">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -ml-32 -mb-32" />

            {/* Binary pattern background */}
            <div className="absolute inset-0 opacity-5 overflow-hidden">
              <div className="text-green-400/20 font-mono text-xs transform rotate-12 scale-150">
                01001000 01100101 01101100 01101100 01101111
                <br />
                01010111 01101111 01110010 01101100 01100100
                <br />
                01000011 01101111 01100100 01100101 01110010
                <br />
              </div>
            </div>

            <div className="relative z-10 text-center">
              <div className="flex flex-col items-center gap-4 mb-6">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-green-400">
                  You need a <span className="text-white">complete system</span>
                  .
                </h3>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-xl md:text-2xl font-bold text-white">
                    We've
                  </span>
                  <span className="px-3 py-1 bg-blue-500/20 rounded-md font-mono text-blue-400 border border-blue-500/30">
                    cracked()
                  </span>
                  <span className="text-xl md:text-2xl font-bold text-white">
                    it.
                  </span>
                </div>

                <div className="flex items-center justify-center gap-2">
                  <span className="text-xl md:text-2xl font-bold text-blue-300">
                    We'll
                  </span>
                  <span className="px-3 py-1 bg-indigo-500/20 rounded-md font-mono text-indigo-400 border border-indigo-500/30">
                    handItOver()
                  </span>
                </div>

                <p className="text-lg md:text-xl text-gray-300 mt-4">
                  In{" "}
                  <span className="text-blue-400 font-semibold font-mono">
                    4.weeks
                  </span>{" "}
                  <span className="text-indigo-400 font-semibold font-mono">
                    8.sessions
                  </span>{" "}
                  <span className="text-purple-400 font-semibold font-mono">
                    0.fluff
                  </span>{" "}
                  <span className="text-green-400 font-semibold font-mono">
                    100.outcomes
                  </span>
                </p>
              </div>

              {/* Feature highlights with tech styling */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 transition-all">
                  <Target className="w-6 h-6 text-blue-400" />
                  <span className="text-blue-300 font-mono text-sm">
                    targeted: true
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/20 transition-all">
                  <Zap className="w-6 h-6 text-purple-400" />
                  <span className="text-purple-300 font-mono text-sm">
                    efficient: true
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition-all">
                  <Lightbulb className="w-6 h-6 text-green-400" />
                  <span className="text-green-300 font-mono text-sm">
                    practical: true
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProblemSolution;
